import { useEffect, useRef, useState } from "react";

function CameraCapture({
  mode,
  title,
  description,
  onConfirm,
  onCancel = null,
  showHeader = true,
  completed = false,
  processing = false,
  processingTitle = "Procesando foto",
  processingMessage = "Estamos procesando la imagen. No hace falta tocar nada.",
  resultMessage = "",
  resultError = false,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const capturedRef = useRef(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [captured, setCaptured] = useState(null);
  const [awaitingResult, setAwaitingResult] = useState(false);

  const preferredFacingMode = mode === "selfie" ? "user" : "environment";

  useEffect(() => {
    startCameraSession({
      preferredFacingMode,
      videoRef,
      streamRef,
      capturedRef,
      setCaptured,
      setError,
      setStatus,
    });

    return () => {
      stopStream(streamRef, videoRef);
      revokePreviewUrl(capturedRef.current);
    };
  }, [preferredFacingMode]);

  function updateCaptured(nextCaptured) {
    capturedRef.current = nextCaptured;
    setCaptured(nextCaptured);
  }

  function restartCamera() {
    setAwaitingResult(false);
    startCameraSession({
      preferredFacingMode,
      videoRef,
      streamRef,
      capturedRef,
      setCaptured: updateCaptured,
      setError,
      setStatus,
    });
  }

  async function captureFrame() {
    if (!videoRef.current || !canvasRef.current) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || (mode === "documento" ? 1536 : 1280);
    canvas.height = video.videoHeight || (mode === "documento" ? 2048 : 720);

    const context = canvas.getContext("2d");
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const mimeType = mode === "documento" ? "image/png" : "image/jpeg";
    const quality = mode === "documento" ? undefined : 0.92;
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, mimeType, quality));

    if (!blob) {
      setError("No se pudo capturar la imagen.");
      setStatus("error");
      return;
    }

    const extension = mimeType === "image/png" ? "png" : "jpg";
    const file = new File([blob], `${mode}-${Date.now()}.${extension}`, { type: mimeType });
    const previewUrl = URL.createObjectURL(blob);

    stopStream(streamRef, videoRef);
    revokePreviewUrl(capturedRef.current);
    updateCaptured({ blob, file, previewUrl });
    setStatus("captured");
  }

  function confirmCapture() {
    if (!captured) {
      return;
    }

    setAwaitingResult(true);
    onConfirm?.(captured.file);
  }

  function cancelCapture() {
    setAwaitingResult(false);
    setError("");
    onCancel?.();
  }

  useEffect(() => {
    if (processing) {
      setAwaitingResult(true);
      return;
    }

    if (resultError || resultMessage) {
      setAwaitingResult(false);
    }
  }, [processing, resultError, resultMessage]);

  const showCapturedActions = status === "captured" && !awaitingResult && !completed;
  const showCaptureProcessing = status === "captured" && processing && !resultError;
  const showCaptureFeedback = status === "captured" && !processing && Boolean(resultMessage);

  return (
    <section className="camera-capture">
      {showHeader ? (
        <div className="camera-header">
          <div>
            <p className="camera-mode">{mode === "selfie" ? "Camara frontal" : "Camara trasera"}</p>
            <h3>{title}</h3>
          </div>
          <p className="camera-description">{description}</p>
        </div>
      ) : null}

      <div className="camera-stage">
        {status === "live" || status === "requesting" ? (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="camera-video"
            aria-label={`Preview ${title}`}
          />
        ) : null}

        {status === "captured" && captured ? (
          <img src={captured.previewUrl} alt={`Captura ${title}`} className="camera-preview" />
        ) : null}

        {status === "requesting" ? <div className="camera-overlay">Solicitando acceso a la camara...</div> : null}
        {status === "error" ? <div className="camera-overlay camera-overlay-error">{error}</div> : null}
      </div>

      {showCaptureProcessing ? (
        <section className="detail-feedback processing-panel camera-feedback">
          <div className="processing-panel-body">
            <span className="processing-spinner" aria-hidden="true" />
            <div>
              <strong>{processingTitle}</strong>
              <p>{processingMessage}</p>
            </div>
          </div>
        </section>
      ) : null}

      {showCaptureFeedback ? (
        <section className={`detail-feedback camera-feedback ${resultError ? "detail-feedback-error" : ""}`}>
          <p>{resultMessage}</p>
        </section>
      ) : null}

      <div className="camera-actions">
        {status === "live" ? (
          <>
            {onCancel ? (
              <button className="secondary-button" type="button" onClick={cancelCapture}>
                Cancelar
              </button>
            ) : null}
            <button className="submit-button camera-action-main" type="button" onClick={captureFrame}>
              Capturar
            </button>
          </>
        ) : null}

        {showCapturedActions ? (
          <>
            {onCancel ? (
              <button className="secondary-button" type="button" onClick={cancelCapture}>
                Cancelar
              </button>
            ) : null}
            <button className="secondary-button" type="button" onClick={restartCamera}>
              Repetir
            </button>
            <button className="submit-button" type="button" onClick={confirmCapture}>
              Confirmar
            </button>
          </>
        ) : null}

        {status === "error" ? (
          <>
            {onCancel ? (
              <button className="secondary-button" type="button" onClick={cancelCapture}>
                Cancelar
              </button>
            ) : null}
            <button className="secondary-button" type="button" onClick={restartCamera}>
              Reintentar
            </button>
          </>
        ) : null}
      </div>

      <canvas ref={canvasRef} className="camera-canvas" />
    </section>
  );
}

function buildConstraints(preferredFacingMode) {
  return [
    {
      audio: false,
      video: {
        facingMode: { ideal: preferredFacingMode },
        width: { ideal: preferredFacingMode === "environment" ? 1920 : 1280 },
        height: { ideal: preferredFacingMode === "environment" ? 1440 : 720 },
        aspectRatio: preferredFacingMode === "environment" ? { ideal: 0.75 } : undefined,
      },
    },
    {
      audio: false,
      video: {
        facingMode: preferredFacingMode,
      },
    },
    {
      audio: false,
      video: true,
    },
  ];
}

function buildCameraErrorMessage(error) {
  if (error?.name === "NotAllowedError" || error?.name === "PermissionDeniedError") {
    return "Permiso de camara denegado.";
  }

  if (error?.name === "NotFoundError" || error?.name === "DevicesNotFoundError") {
    return "No se encontro una camara disponible.";
  }

  return "No se pudo acceder a la camara.";
}

async function startCameraSession({
  preferredFacingMode,
  videoRef,
  streamRef,
  capturedRef,
  setCaptured,
  setError,
  setStatus,
}) {
  stopStream(streamRef, videoRef);
  revokePreviewUrl(capturedRef.current);
  capturedRef.current = null;
  setCaptured(null);
  setError("");
  setStatus("requesting");

  if (!navigator.mediaDevices?.getUserMedia) {
    setStatus("error");
    setError("La camara no esta disponible en este dispositivo o navegador.");
    return;
  }

  const constraints = buildConstraints(preferredFacingMode);

  for (const constraint of constraints) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraint);
      streamRef.current = stream;
      attachStream(videoRef, stream, setStatus, setError);
      setStatus("live");
      return;
    } catch (cameraError) {
      if (constraint === constraints[constraints.length - 1]) {
        setStatus("error");
        setError(buildCameraErrorMessage(cameraError));
      }
    }
  }
}

function attachStream(videoRef, stream, setStatus, setError) {
  if (!videoRef.current) {
    return;
  }

  videoRef.current.srcObject = stream;
  videoRef.current.play().catch(() => {
    setStatus("error");
    setError("No se pudo iniciar la previsualizacion de la camara.");
  });
}

function stopStream(streamRef, videoRef) {
  if (streamRef.current) {
    streamRef.current.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }

  if (videoRef.current) {
    videoRef.current.srcObject = null;
  }
}

function revokePreviewUrl(captured) {
  if (captured?.previewUrl) {
    URL.revokeObjectURL(captured.previewUrl);
  }
}

export default CameraCapture;
